/**
 * The `SearchCriteria` form shared by C-Search's Probe and Compare tabs.
 *
 * It builds the wire contract from `@numeo/types` as-is and checks only what the operator can fix before sending
 * (ADR-040.1 §6.9 limits). The service is the validator of record: a request this form lets through and the service
 * refuses comes back as the service's own 400 message, which is the more useful answer in an audit.
 */

import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Card from '@mui/joy/Card'
import FormControl from '@mui/joy/FormControl'
import FormLabel from '@mui/joy/FormLabel'
import Input from '@mui/joy/Input'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Typography from '@mui/joy/Typography'
import { SEARCH_LIMITS, UNIFIED_EQUIPMENT_TYPES, type SearchCriteria, type SearchPlace, type SearchSort } from '@numeo/types-core'
import * as React from 'react'

type PlaceMode = 'none' | 'point' | 'states'

interface PlaceState {
    mode: PlaceMode
    lat: string
    lon: string
    radius: string
    states: string
}

interface SearchFormValue {
    projectName: string
    origin: PlaceState
    destination: PlaceState
    equipment: string[]
    pickupFrom: string
    pickupTo: string
    maxPostedAgeHours: string
    maxWeightLbs: string
    maxLengthFt: string
    fullPartial: '' | 'FULL' | 'PARTIAL'
    sources: string
    sort: SearchSort
}

const isoDay = (offsetDays: number): string => new Date(Date.now() + offsetDays * 86_400_000).toISOString().slice(0, 10)

/** Chicago → anywhere, dry van, the next three days: a lane busy enough that an empty answer means something. */
export function defaultSearchForm(): SearchFormValue {
    return {
        projectName: 'admin',
        origin: { mode: 'point', lat: '41.8781', lon: '-87.6298', radius: '100', states: '' },
        destination: { mode: 'none', lat: '', lon: '', radius: '100', states: '' },
        equipment: ['DRY_VAN'],
        pickupFrom: isoDay(0),
        pickupTo: isoDay(2),
        maxPostedAgeHours: '',
        maxWeightLbs: '',
        maxLengthFt: '',
        fullPartial: '',
        sources: '',
        sort: 'NEWEST',
    }
}

const optionalNumber = (raw: string): number | undefined => (raw.trim() === '' ? undefined : Number(raw))
const list = (raw: string): string[] =>
    raw
        .split(/[\s,]+/)
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean)

function toPlace(place: PlaceState, label: string, maxRadius: number): { place?: SearchPlace; error?: string } {
    if (place.mode === 'none') return {}
    if (place.mode === 'states') {
        const states = list(place.states)
        return states.length ? { place: { states } } : { error: `${label}: name at least one state` }
    }
    const lat = Number(place.lat)
    const lon = Number(place.lon)
    const radiusMiles = Number(place.radius)
    if (!Number.isFinite(lat) || !Number.isFinite(lon) || place.lat === '' || place.lon === '')
        return { error: `${label}: latitude and longitude are required` }
    if (!(radiusMiles > 0) || radiusMiles > maxRadius) return { error: `${label}: radius must be 1–${maxRadius} mi for this sort` }
    return { place: { point: { lat, lon }, radiusMiles } }
}

/** The form as the wire contract, or the first thing the operator has to fix. */
export function toCriteria(form: SearchFormValue): { criteria?: SearchCriteria; error?: string } {
    const maxRadius = form.sort === 'NEWEST' ? SEARCH_LIMITS.radiusMilesNewest : SEARCH_LIMITS.radiusMilesPriced
    const origin = toPlace(form.origin, 'Origin', maxRadius)
    const destination = toPlace(form.destination, 'Destination', maxRadius)
    if (origin.error || destination.error) return { error: origin.error ?? destination.error }
    if (!origin.place && !destination.place) return { error: 'Set an origin, a destination, or both' }
    if (form.equipment.length === 0) return { error: 'Pick at least one equipment type' }
    if (!form.pickupFrom || !form.pickupTo || form.pickupTo < form.pickupFrom) return { error: 'Pickup "to" must be on or after "from"' }
    const days = (Date.parse(form.pickupTo) - Date.parse(form.pickupFrom)) / 86_400_000 + 1
    if (days > SEARCH_LIMITS.pickupRangeDays) return { error: `Pickup range is at most ${SEARCH_LIMITS.pickupRangeDays} days` }
    const sources = list(form.sources)
    return {
        criteria: {
            ...(origin.place ? { origin: origin.place } : {}),
            ...(destination.place ? { destination: destination.place } : {}),
            equipment: form.equipment,
            pickup: { from: form.pickupFrom, to: form.pickupTo },
            maxWeightLbs: optionalNumber(form.maxWeightLbs),
            maxLengthFt: optionalNumber(form.maxLengthFt),
            maxPostedAgeHours: optionalNumber(form.maxPostedAgeHours),
            ...(form.fullPartial ? { fullPartial: form.fullPartial } : {}),
            ...(sources.length ? { sources } : {}),
        },
    }
}

function PlaceFields({ label, value, onChange }: { label: string; value: PlaceState; onChange: (v: PlaceState) => void }) {
    return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <FormControl size="sm">
                <FormLabel>{label}</FormLabel>
                <Select size="sm" value={value.mode} onChange={(_, mode) => mode && onChange({ ...value, mode })} sx={{ minWidth: 120 }}>
                    <Option value="none">Anywhere</Option>
                    <Option value="point">Point + radius</Option>
                    <Option value="states">States</Option>
                </Select>
            </FormControl>
            {value.mode === 'point' ? (
                <>
                    <Input size="sm" placeholder="lat" value={value.lat} onChange={(e) => onChange({ ...value, lat: e.target.value })} sx={{ width: 100 }} />
                    <Input size="sm" placeholder="lon" value={value.lon} onChange={(e) => onChange({ ...value, lon: e.target.value })} sx={{ width: 100 }} />
                    <Input
                        size="sm"
                        placeholder="mi"
                        endDecorator="mi"
                        value={value.radius}
                        onChange={(e) => onChange({ ...value, radius: e.target.value })}
                        sx={{ width: 90 }}
                    />
                </>
            ) : null}
            {value.mode === 'states' ? (
                <Input
                    size="sm"
                    placeholder="IL, IN, OH"
                    value={value.states}
                    onChange={(e) => onChange({ ...value, states: e.target.value })}
                    sx={{ width: 220 }}
                />
            ) : null}
        </Box>
    )
}

export function SearchCriteriaForm({
    value,
    onChange,
    onSubmit,
    submitting,
    submitLabel,
    showSort,
}: {
    value: SearchFormValue
    onChange: (v: SearchFormValue) => void
    onSubmit: () => void
    submitting: boolean
    submitLabel: string
    /** Compare always runs newest-first on both sides, so it hides the sort. */
    showSort: boolean
}) {
    const set = <K extends keyof SearchFormValue>(key: K, v: SearchFormValue[K]) => onChange({ ...value, [key]: v })
    const { error } = toCriteria(value)

    return (
        <Card variant="outlined" sx={{ mb: 2, gap: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <PlaceFields label="Origin" value={value.origin} onChange={(v) => set('origin', v)} />
                <PlaceFields label="Destination" value={value.destination} onChange={(v) => set('destination', v)} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <FormControl size="sm">
                    <FormLabel>Equipment</FormLabel>
                    <Select
                        size="sm"
                        multiple
                        value={value.equipment}
                        onChange={(_, v) => set('equipment', v as string[])}
                        sx={{ minWidth: 200 }}
                        renderValue={(selected) => selected.map((s) => s.value).join(', ')}
                    >
                        {UNIFIED_EQUIPMENT_TYPES.map((t) => (
                            <Option key={t.code} value={t.code}>
                                {t.label}
                            </Option>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>Pickup from</FormLabel>
                    <Input size="sm" type="date" value={value.pickupFrom} onChange={(e) => set('pickupFrom', e.target.value)} />
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>Pickup to</FormLabel>
                    <Input size="sm" type="date" value={value.pickupTo} onChange={(e) => set('pickupTo', e.target.value)} />
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>Posted within (h)</FormLabel>
                    <Input
                        size="sm"
                        placeholder="any"
                        value={value.maxPostedAgeHours}
                        onChange={(e) => set('maxPostedAgeHours', e.target.value)}
                        sx={{ width: 110 }}
                    />
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>Max weight (lb)</FormLabel>
                    <Input size="sm" value={value.maxWeightLbs} onChange={(e) => set('maxWeightLbs', e.target.value)} sx={{ width: 110 }} />
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>Max length (ft)</FormLabel>
                    <Input size="sm" value={value.maxLengthFt} onChange={(e) => set('maxLengthFt', e.target.value)} sx={{ width: 100 }} />
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>Full / partial</FormLabel>
                    <Select
                        size="sm"
                        value={value.fullPartial}
                        onChange={(_, v) => set('fullPartial', (v ?? '') as SearchFormValue['fullPartial'])}
                        sx={{ minWidth: 100 }}
                    >
                        <Option value="">Both</Option>
                        <Option value="FULL">Full</Option>
                        <Option value="PARTIAL">Partial</Option>
                    </Select>
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>Sources</FormLabel>
                    <Input size="sm" placeholder="all" value={value.sources} onChange={(e) => set('sources', e.target.value)} sx={{ width: 180 }} />
                </FormControl>
                {showSort ? (
                    <FormControl size="sm">
                        <FormLabel>Sort</FormLabel>
                        <Select size="sm" value={value.sort} onChange={(_, v) => v && set('sort', v)} sx={{ minWidth: 150 }}>
                            <Option value="NEWEST">Newest</Option>
                            <Option value="RATE">Rate</Option>
                            <Option value="RATE_PER_MILE">Rate per mile</Option>
                            <Option value="PICKUP">Pickup</Option>
                        </Select>
                    </FormControl>
                ) : null}
                <FormControl size="sm">
                    <FormLabel>Search as project</FormLabel>
                    <Input size="sm" value={value.projectName} onChange={(e) => set('projectName', e.target.value)} sx={{ width: 140 }} />
                </FormControl>
                <Button size="sm" onClick={onSubmit} loading={submitting} disabled={Boolean(error)}>
                    {submitLabel}
                </Button>
            </Box>
            {error ? (
                <Typography level="body-xs" color="warning">
                    {error}
                </Typography>
            ) : (
                <Typography level="body-xs" textColor="text.tertiary">
                    Project decides which private listings are visible — nothing is written, metered or attributed to it.
                </Typography>
            )}
        </Card>
    )
}
